import React, { useEffect, useRef, useState } from 'react';
import heroVideo from '@/assets/homepage/background_video.mp4';
import { heroContent } from '@/content/hero';
import { Button, Icon, Reveal } from '@/components/ui';

const Hero: React.FC = () => {
    // The video always autoplays by default — exactly like the original,
    // pre-accessibility markup (`autoPlay muted loop playsInline`, no
    // conditional logic) — so it is never silently disabled by an OS/browser
    // `prefers-reduced-motion` setting (many visitors, including testers on
    // macOS, have "Reduce Motion" on for battery/accessibility reasons
    // unrelated to this specific video, which previously made the hero look
    // "broken" for them). Accessibility (WCAG 2.2.2 "Pause, Stop, Hide") is
    // instead satisfied purely additively via the visible play/pause toggle
    // button below, which works for every visitor regardless of that
    // preference.
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // `.play()` returns a promise that rejects if the browser blocks
        // autoplay outright (e.g. Data Saver / Low Power Mode); reflect that
        // in the toggle button instead of leaving it out of sync — the
        // gradient background (`.hero`) still shows underneath either way.
        // This re-asserts playback in case the native `autoPlay` attribute
        // was ignored (e.g. a late-attached `src`), it never blocks it.
        video
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => setIsPlaying(false));
    }, []);

    const handleToggle = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            video
                .play()
                .then(() => setIsPlaying(true))
                .catch(() => setIsPlaying(false));
        } else {
            video.pause();
            setIsPlaying(false);
        }
    };

    return (
        <section id="home" className="hero">
            <video ref={videoRef} autoPlay muted loop playsInline className="hero-video">
                <source src={heroVideo} type="video/mp4" />
            </video>
            <button
                type="button"
                className="hero-video-toggle"
                onClick={handleToggle}
                aria-label={isPlaying ? 'Pause background video' : 'Play background video'}
            >
                <Icon name={isPlaying ? 'pause' : 'play'} aria-hidden />
            </button>
            <Reveal as="div" className="hero-content">
                <h1 className="hero-headline">{heroContent.headline}</h1>
                <p className="hero-description">{heroContent.description}</p>
                <div className="hero-btns">
                    {heroContent.ctas.map((cta) => (
                        <Button key={cta.href} as="a" href={cta.href} className={cta.className}>
                            {cta.label}
                        </Button>
                    ))}
                </div>
            </Reveal>
        </section>
    );
};

export default Hero;
